<template>
  <div class="container mt-5 px-2">
    <article class="message is-danger" v-if="!token">
      <div class="message-body">
        You have not signed in yet. Please go back to <a href="/">home page</a> to sign in first.
      </div>
    </article>
    <div v-if="token">
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link :to="'/sales-monitor'">Sales monitor</router-link></li>
          <li>
            <a href="#" aria-current="page">
              <span>{{itemId}}</span> &nbsp;-&nbsp;
              <span>{{itemSku}}</span>
            </a>
          </li>
        </ul>
      </nav>

      <div class="mt-3">
        <div v-if="waiting">
          <span class="icon is-medium is-size-4">
            <i class="fas fa-spinner fa-pulse"></i>
          </span>
        </div>
        <div v-else>
          <div>
            <h1 class="title is-5 mt-6">Year's sales dashboard</h1>
            <div class="table-container">
              <table class="table is-fullwidth is-hoverable my-table">
                <thead>
                  <th style="width:150px">
                    <span>Country</span>
                  </th>
                  <th>
                    <span>Inventory</span>
                  </th>
                  <th class="has-text-right">
                    <span>One week sales</span>
                  </th>
                  <th class="has-text-right">
                    <span>One week days</span>
                  </th>
                  <th class="has-text-right">
                    <span>One week speed</span>
                  </th>
                  <th class="has-text-right">
                    <span>Two weeks sales</span>
                  </th>
                  <th class="has-text-right">
                    <span>Two week days</span>
                  </th>
                  <th class="has-text-right">
                    <span>Two weeks speed</span>
                  </th>
                  <th class="has-text-right">
                    <span>One month sales</span>
                  </th>
                  <th class="has-text-right">
                    <span>One year max speed</span>
                  </th>
                  <th class="has-text-right">
                    <span>Speed increase rate (%)</span>
                  </th>
                  <th class="has-text-right">
                    <span>Sales Potential (%)</span>
                  </th>
                  <th class="has-text-right">
                    <span>Coverage in weeks</span>
                  </th>
                  <th>
                    <span>OOS in days</span>
                  </th>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in showingItems" :key="'product-' + i">
                    <td :class="{'has-text-weight-bold': p.itemId, 'has-text-right': !p.itemId}">
                      <span>{{p.name}}</span>
                    </td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.inventory }}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.w1.quantity}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.w1.days}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ roundTo(p.w1.speed, 3)}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.w2.quantity}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.w2.days}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ roundTo(p.w2.speed, 3)}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.w4.quantity}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ roundTo(p.year, 3)}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.increase}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ p.potential}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ roundTo(p.coverage, 0)}}</td>
                    <td class="has-text-right" :class="{'has-text-weight-bold': p.itemId}">{{ roundTo(p.remaining, 0)}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <div class="table-section">
              <h1 class="title is-5 mt-6">Year's weeks sales</h1>
              <div class="tabs">
                <ul>
                  <li :class="{'is-active': tab == 'US - Home Depot'}">
                    <a @click="tab = 'US - Home Depot'">US - Home Depot</a>
                  </li>
                  <li :class="{'is-active': tab == 'US - Wayfair'}">
                    <a @click="tab = 'US - Wayfair'">US - Wayfair</a>
                  </li>
                  <li :class="{'is-active': tab == 'US - Lowes'}">
                    <a @click="tab = 'US - Lowes'">US - Wayfair</a>
                  </li>
                  <li :class="{'is-active': tab == 'Canada - Home Depot'}">
                    <a @click="tab = 'Canada - Home Depot'">Canada - Home Depot</a>
                  </li>
                  <li :class="{'is-active': tab == 'Canada - Wayfair'}">
                    <a @click="tab = 'Canada - Wayfair'">Canada - Wayfair</a>
                  </li>
                  <li :class="{'is-active': tab == 'Canada - Lowes'}">
                    <a @click="tab = 'Canada - Lowes'">Canada - Wayfair</a>
                  </li>
                </ul>
              </div>
              <div class="table-container">
                <table class="table is-fullwidth is-hoverable my-table">
                  <thead>
                    <th>
                      <span>#</span>
                    </th>
                    <th>
                      <span>Week</span>
                    </th>
                    <th class="has-text-right">
                      <span>{{ tabCountry + ' sales' }}</span>
                    </th>
                    <th class="has-text-right">
                      <span>{{ tabCountry + ' days' }}</span>
                    </th>
                    <th class="has-text-right">
                      <span>{{ tabCountry + ' speed' }}</span>
                    </th>
                    <th class="has-text-right">
                      <span>{{ tabPlatform + ' sales' }}</span>
                    </th>
                    <th class="has-text-right">
                      <span>{{ tabPlatform + ' days' }}</span>
                    </th>
                    <th class="has-text-right">
                      <span>{{ tabPlatform + ' speed' }}</span>
                    </th>
                  </thead>
                  <tbody>
                    <tr v-for="(s, i) in showingWeekSales" :key="'ws-' + i">
                      <td>
                        <span>{{i + 1}}</span>
                      </td>
                      <td>
                        <span>{{s.week}}</span>
                      </td>
                      <td class="has-text-right">{{ s.all.quantity }}</td>
                      <td class="has-text-right">{{ s.all.days }}</td>
                      <td class="has-text-right">{{ roundTo(s.all.speed, 3) }}</td>
                      <td class="has-text-right">{{ s.platform.quantity }}</td>
                      <td class="has-text-right">{{ s.platform.days }}</td>
                      <td class="has-text-right">{{ roundTo(s.platform.speed, 3) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
      <div v-if="error" class="notification is-danger is-light">
        <button class="delete" @click="error=''"></button>
        {{error}}
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'SalesSummary',
  data () {
    return {
      error: '',
      waiting: false,
      summary: null,
      weekSales: [],
      tab: 'US - Home Depot'
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    token () {
      return this.$store.state.user.token
    },
    itemId () {
      return this.$route.params.itemId
    },
    itemSku () {
      if (!this.summary) {
        return ''
      }
      return this.summary.itemSku
    },
    showingItems () {
      if (!this.summary) {
        return []
      }
      var items = []
      var usItem = this.makeItem(this.summary, 'US', 'all')
      items.push(usItem)
      var platforms = ['hd', 'wf', 'lowes']
      platforms.forEach(platform => {
        var pItem = this.makeItem(this.summary, 'US', platform)
        items.push(pItem)
      })
      var canadaItem = this.makeItem(this.summary, 'Canada', 'all')
      items.push(canadaItem)
      platforms.forEach(platform => {
        var pItem = this.makeItem(this.summary, 'Canada', platform)
        items.push(pItem)
      })
      return items
    },
    tabCountry () {
      var ss = this.tab.split(' - ')
      return ss[0]
    },
    tabPlatform () {
      var ss = this.tab.split(' - ')
      return ss[1]
    },
    tabPlatformId () {
      var platforms = {'Home Depot': 'hd', 'Wayfair': 'wf', 'Lowes': 'lowes'}
      return platforms[this.tabPlatform]
    },
    showingWeekSales () {
      return this.weekSales.map(ws => {
        var s = ws[this.tabCountry.toLowerCase() + 'Sales']
        return {
          week: ws.weekStart,
          all: s.all,
          platform: s[this.tabPlatformId]
        }
      })
    },
  },
  watch: {
    itemId: function (val) {
      this.getData()
    },
  },
  methods: {
    makeItem (p, country, platform) {
      var platforms = {'hd': 'Home Depot', 'wf': 'Wayfair', 'lowes': 'Lowes'}
      var name = platform == 'all' ? country : platforms[platform]
      var itemId = platform == 'all' ? p.itemId : ''
      var prefix = country.toLowerCase() + 'Sales'
      var item = {
        name: name,
        itemId: itemId,
        inventory: p[prefix]['inventory'][platform],
        w1: p[prefix]['1w'][platform],
        w2: p[prefix]['2w'][platform],
        w4: p[prefix]['4w'][platform],
        year: p[prefix]['yearMax'][platform],
        increase: p[prefix]['speedIncrease'][platform],
        potential: p[prefix]['potential'][platform],
        coverage: p[prefix]['coverage'][platform],
        remaining: p[prefix]['remaining'][platform],
      }
      return item
    },
    getData () {
      this.waiting = true
      this.$http.get(this.server + '/myapp/get-summary-by-item-id/' + this.itemId).then(resp => {
        this.summary = resp.body.summary
        this.weekSales = resp.body.weekSales
        this.waiting = false
      }, err => {
        this.waiting = false
        this.error = err.body
      })
    },
    roundTo (value, decimals) {
      if (typeof(value) == 'string') {
        return value
      }
      if (!value) {
        return 0
      }
      if (decimals == 0) {
        return Math.round(value)
      }
      var tens = Math.pow(10, decimals)
      return Math.round((value + Number.EPSILON) * tens) / tens
    },
  },
  mounted () {
    this.$nextTick(function(){
      this.getData()
    })
  },
}
</script>

<style scoped>
.table-section {
  overflow-x: auto;
}
</style>
